import { useCallback, useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { MdClose, MdModeEdit } from "react-icons/md";
import { aging, qc } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Pagination from "../../common/Pagination";
import Button from "../../common/Button";
import { IoIosSave } from "react-icons/io";
import { AiOutlineCheck } from "react-icons/ai";
import Table from "../../common/Table";
import { LayoutGrid } from "lucide-react";
import { RiHistoryLine } from "react-icons/ri";
import Loader from "../../common/Loader";
import { formatDate } from "../../utlis/formateDate";

const QC = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isQcData, setIsQcData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [editingRow, setEditingRow] = useState(null);
  const [backupRow, setBackupRow] = useState(null);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [isQcDoneData, setIsQcDoneData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);

  const toInt = (v) =>
    v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0;
  const toFloat = (v) =>
    v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0;
  const getQcId = (r) => toInt(r.qC_Id ?? r.QC_Id ?? 0);

  const isRowEditable = (index) => editingRow === index;

  const normalizeRow = (r) => ({
    ...r,
    qc_Pcs: r.qc_Pcs ?? "",
    rejected_Pcs: r.rejected_Pcs ?? "",
    approved_Pcs: r.approved_Pcs ?? "",
    first_Piece: r.first_Piece ?? "",
    first_Weight: r.first_Weight ?? "",
    second_Piece: r.second_Piece ?? "",
    second_Weight: r.second_Weight ?? "",
    remark: r.remark ?? r.remarks ?? "",
  });

  const handleEdit = (rowIndex) => {
    setBackupRow(JSON.parse(JSON.stringify(isQcData[rowIndex])));
    setEditingRow(rowIndex);
  };

  const handleCancelEdit = (rowIndex) => {
    if (backupRow) {
      const updatedData = [...isQcData];
      updatedData[rowIndex] = backupRow;
      setIsQcData(updatedData);
    }
    setEditingRow(null);
    setBackupRow(null);
  };

  const fetchData = useCallback(async (page, tab) => {
    if (loading) return;
    setLoading(true);
    try {
      let res;
      if (tab === "PENDING") {
        res = await qc.getAllQC(page, pageSize);
        const rows = (res.data || []).map(normalizeRow);
        setIsQcData((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(rows.length === pageSize);
      } else {
        res = await aging.getAllQcDone(page, pageSize);
        const rows = res.data || [];
        setIsQcDoneData((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(rows.length === pageSize);
      }
    } catch (error) {
      notify("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchData(1, activeTab);
  }, [activeTab, fetchData]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 10 && hasMore && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchData(nextPage, activeTab);
    }
  };

  const handleSubmit = async (rowIndex) => {
    try {
      const row = isQcData[rowIndex];
      const payload = {
        qC_Id: getQcId(row),
        batch_Id: row.batch_Id ?? "",
        casting_No: row.casting_No ?? "",
        order_No: row.order_No,
        die_Id: row.die_Id ?? "",
        produced_Pcs: toInt(row.produced_Pcs),
        qc_Pcs: toInt(row.qc_Pcs),
        rejected_Pcs: toInt(row.rejected_Pcs),
        reject_Reason: row.reject_Reason,
        approved_Pcs: toInt(row.approved_Pcs),
        first_Piece: toInt(row.first_Piece),
        first_Weight: toFloat(row.first_Weight),
        second_Piece: toInt(row.second_Piece),
        second_Weight: toFloat(row.second_Weight),
        remark: row.remark ?? row.remarks ?? "",
        production_Seq_No: row.production_Seq_No,
        created_By: userId,
      };

      await qc.saveOrUpdate(payload);
      notify("Saved successfully", "success");
      setEditingRow(null);
      setBackupRow(null);
      setCurrentPage(1);
      fetchData(1, activeTab);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to save";
      notify(msg, "error");
    }
  };

  const handleMarkDone = async (rowIndex) => {
    try {
      const row = isQcData[rowIndex];

      if (toInt(row.qc_Pcs) !== toInt(row.produced_Pcs)) {
        notify("QC Pcs must be equal to Produced Pcs to mark as done", "error");
        return;
      }

      if (
        toInt(row.produced_Pcs) !==
        toInt(row.approved_Pcs) + toInt(row.rejected_Pcs)
      ) {
        notify("Total (Appr + Rej) must match Produced Pcs", "error");
        return;
      }

      const payload = {
        die_Id: row.die_Id,
        LastPCSWeight:
          (toFloat(row.first_Weight) + toFloat(row.second_Weight)) / 2,
        created_By: userId,
        production_Seq_No: row.production_Seq_No,
      };

      await qc.MarkQCDone(payload);
      notify("Production marked as done", "success");

      setCurrentPage(1);
      fetchData(1, "PENDING");
    } catch (error) {
      notify("Failed to mark done", "error");
    }
  };

  const handleRowChange = (rowIndex, field, raw) => {
    setIsQcData((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row[field] = raw;

      const qcPcs = toInt(field === "qc_Pcs" ? raw : row.qc_Pcs);
      const rejPcs = toInt(field === "rejected_Pcs" ? raw : row.rejected_Pcs);
      row.approved_Pcs = String(Math.max(qcPcs - rejPcs, 0));

      next[rowIndex] = row;
      return next;
    });
  };

  const renderCell = (
    row,
    rowIndex,
    field,
    { type = "text", step, alignRight = true } = {},
  ) => {
    const editable = isRowEditable(rowIndex);

    if (editable) {
      return (
        <input
          type={type}
          step={step}
          value={row[field] ?? ""}
          onChange={(e) => handleRowChange(rowIndex, field, e.target.value)}
          className={`w-full bg-white border border-blue-400 rounded px-1.5 py-1 outline-none focus:ring-1 focus:ring-blue-500 text-[12px] shadow-sm ${alignRight ? "text-right" : "text-left"
            }`}
          autoFocus={field === "qc_Pcs"}
        />
      );
    }

    return (
      <div
        className={`px-2 py-1 text-slate-700 font-medium ${alignRight ? "text-right" : "text-left"}`}
      >
        {row[field] || row[field] === 0 ? row[field] : "-"}
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
            {activeTab === "PENDING"
              ? "QC Inspection Pending"
              : "Quality Control Completed"}
          </h2>
          <p className="text-sm text-slate-500 font-medium">
            {activeTab === "PENDING"
              ? "Verify production counts and record quality parameters for pending dies."
              : "Review the history of approved production and quality check records."}
          </p>
        </div>
        <div className="inline-flex p-1 bg-slate-200/50 rounded-xl backdrop-blur-sm">
          <button
            onClick={() => {
              setActiveTab("PENDING");
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "PENDING"
              ? "bg-white text-primary shadow-sm"
              : "text-slate-600 hover:text-slate-800"
              }`}
          >
            <LayoutGrid size={16} /> QC Pending
          </button>
          <button
            onClick={() => {
              setActiveTab("DONE");
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "DONE"
              ? "bg-white text-primary shadow-sm"
              : "text-slate-600 hover:text-slate-800"
              }`}
          >
            <RiHistoryLine size={16} /> QC Done
          </button>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
        style={{ height: "calc(100vh - 320px)" }}
      >
        {activeTab === "PENDING" && (
          <div className="relative shadow-sm overflow-hidden">
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-340px)]">
              <table className="w-full text-[13px] text-left ">
                <thead className="bg-gray-50 sticky top-0 z-30 shadow-sm">
                  <tr className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                    <th className="p-3 border-b border-gray-200 text-center">
                      SO No
                    </th>
                    <th className="p-3 border-b border-gray-200">Batch</th>
                    <th className="p-3 border-b border-gray-200">Casting</th>
                    <th className="p-3 border-b border-gray-200">Die</th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      Prod.
                    </th>
                    <th className="p-3 border-b border-gray-200 text-left">
                      Dispatch Date
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      QC
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      Rej.
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      Appr.
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      1st Wt
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      2nd Wt
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      Curr Wt
                    </th>
                    <th className="p-3 border-b border-gray-200 text-right">
                      Cut Len
                    </th>
                    <th className="p-3 border-b border-gray-200">
                      Reason/Remark
                    </th>
                    <th className="p-3 border-b border-gray-200 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isQcData.map((row, index) => (
                    <tr
                      key={index}
                      className={`transition-colors duration-150 ${isRowEditable(index) ? "bg-blue-50/50" : "hover:bg-gray-50"}`}
                    >
                      <td className="p-2 text-center font-bold text-slate-800">
                        {row.order_No}
                      </td>
                      <td className="p-2 text-gray-500">{row.batch_Id}</td>
                      <td className="p-2 font-semibold text-blue-600">
                        {row.casting_No}
                      </td>
                      <td className="p-2 font-semibold text-blue-600">
                        {row.dieNumber}
                      </td>
                      <td className="p-2 text-right font-semibold text-slate-600">
                        {row.produced_Pcs}
                      </td>
                      <td className="p-2 text-left text-slate-600">
                        {formatDate(row.dispatch_Date)}
                      </td>

                      <td className="p-2">
                        {renderCell(row, index, "qc_Pcs", { type: "number" })}
                      </td>
                      <td className="p-2">
                        {renderCell(row, index, "rejected_Pcs", {
                          type: "number",
                        })}
                      </td>
                      <td className="p-2 text-right font-bold text-green-600">
                        {row.approved_Pcs}
                      </td>

                      <td className="p-2">
                        {renderCell(row, index, "first_Weight", {
                          type: "number",
                          step: "0.001",
                        })}
                      </td>
                      <td className="p-2">
                        {renderCell(row, index, "second_Weight", {
                          type: "number",
                          step: "0.001",
                        })}
                      </td>
                      <td className="p-2 text-right text-slate-600">
                        {row.current_Weight || "-"}
                      </td>
                      <td className="p-2 text-right text-slate-600">
                        {row.cut_Length || "-"}
                      </td>

                      <td className="p-2">
                        {isRowEditable(index) ? (
                          <div className="flex flex-col gap-1">
                            {renderCell(row, index, "reject_Reason", {
                              alignRight: false,
                            })}
                            {renderCell(row, index, "remark", {
                              alignRight: false,
                            })}
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-500">
                            {row.reject_Reason && (
                              <div>R: {row.reject_Reason}</div>
                            )}
                            {row.remark && <div>N: {row.remark}</div>}
                          </div>
                        )}
                      </td>

                      <td className="p-2">
                        <div className="flex items-center justify-center gap-2">
                          {isRowEditable(index) ? (
                            <>
                              <button
                                onClick={() => handleSubmit(index)}
                                className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                              >
                                <IoIosSave size={16} />
                              </button>
                              <button
                                onClick={() => handleCancelEdit(index)}
                                className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                <MdClose size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(index)}
                                className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                              >
                                <MdModeEdit size={16} />
                              </button>
                              <button
                                onClick={() => handleMarkDone(index)}
                                className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                              >
                                <AiOutlineCheck size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === "DONE" && (
          <>
            {loading ? (
              <Loader />
            ) : (
              <div className="min-w-max">
                <Table
                  columns={[
                    { key: "order_No", label: "Order" },
                    { key: "batch_Id", label: "Batch" },
                    { key: "casting_No", label: "Casting" },
                    { key: "dieNumber", label: "Die" },
                    { key: "approved_Pcs", label: "Appr Pcs" },
                    { key: "current_Weight", label: "Current Wt" },
                    { key: "cut_Length", label: "Cut Length" },
                    {
                      key: "dispatch_Date",
                      label: "Dispatch Date",
                      render: (value) => formatDate(value),
                    },
                    {
                      key: "packing_Instructions",
                      label: "Packing Inst",
                    },
                    { key: "general_Notes", label: "General Notes" },
                  ]}
                  data={isQcDoneData}
                  showActions={false}
                />
              </div>
            )}
          </>
        )}{" "}
      </div>
      <div className="flex justify-center mt-6">
        <div className="bg-[#1e293b] text-white px-8 py-3 rounded-2xl shadow-2xl flex items-center gap-12 border border-slate-700">
          <div className="flex flex-col items-center">
            <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
              {activeTab === "PENDING" ? "Qc" : "Total Approved"}
            </span>
            <span className="text-xl font-black">
              {activeTab === "PENDING"
                ? isQcData
                  .reduce((sum, row) => sum + toInt(row.produced_Pcs), 0)
                  .toLocaleString()
                : isQcDoneData
                  .reduce((sum, row) => sum + toInt(row.approved_Pcs), 0)
                  .toLocaleString()}
            </span>
          </div>

          {activeTab === "PENDING" && (
            <>
              <div className="h-8 w-[1px] bg-slate-600"></div>

              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-red-400 font-bold">
                  Rejected
                </span>
                <span className="text-xl font-black text-red-400">
                  {isQcData
                    .reduce((sum, row) => sum + toInt(row.rejected_Pcs), 0)
                    .toLocaleString()}
                </span>
              </div>

              <div className="h-8 w-[1px] bg-slate-600"></div>

              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-green-400 font-bold">
                  Approved
                </span>
                <span className="text-xl font-black text-green-400">
                  {isQcData
                    .reduce((sum, row) => sum + toInt(row.approved_Pcs), 0)
                    .toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QC;
